// src/components/watchlist/AddSubscription.js
import React, { useState, useRef, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Search as SearchIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Map as MapIcon,
  LocationOn as LocationOnIcon,
  Home as HomeIcon,
  Apartment as ApartmentIcon,
  NotificationsActive as NotificationsActiveIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import LocationSearch from './LocationSearch'
import LocationMap from './LocationMap'

// Constants
const PRICE_RENT_MIN_DEFAULT = 1000
const PRICE_RENT_MAX_DEFAULT = 2500
const PRICE_BUY_MIN_DEFAULT = 300000
const PRICE_BUY_MAX_DEFAULT = 500000

const AddSubscription = ({ user, onCancel, onSuccess, showAlert }) => {
  const { t } = useTranslation()

  // State
  const [activeStep, setActiveStep] = useState(0)
  const [mode, setMode] = useState('map')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [propertyType, setPropertyType] = useState('rent')
  const [showPriceRange, setShowPriceRange] = useState(false)
  const [customPriceMin, setCustomPriceMin] = useState(null)
  const [customPriceMax, setCustomPriceMax] = useState(null)
  const [loading, setLoading] = useState(false)

  // Effect to set default price range based on property type
  useEffect(() => {
    if (showPriceRange) {
      if (propertyType === 'rent') {
        setCustomPriceMin(PRICE_RENT_MIN_DEFAULT)
        setCustomPriceMax(PRICE_RENT_MAX_DEFAULT)
      } else {
        setCustomPriceMin(PRICE_BUY_MIN_DEFAULT)
        setCustomPriceMax(PRICE_BUY_MAX_DEFAULT)
      }
    }
  }, [propertyType, showPriceRange])

  // Handle mode change between search and map
  const handleModeChange = (event, newMode) => {
    if (newMode) {
      setMode(newMode)
      setSelectedLocation(null)
      setSearchQuery('')
      setSearchSuggestions([])
    }
  }

  // Handle location selection from search or map
  const handleLocationSelected = (location) => {
    setSelectedLocation(location)
    setActiveStep(1)
  }

  // Handle property type change
  const handlePropertyTypeChange = (event, newType) => {
    if (newType !== null) {
      setPropertyType(newType)
    }
  }

  // Handle address name change when using custom polygon
  const handleAddressChange = (e) => {
    if (!selectedLocation) return
    setSelectedLocation({
      ...selectedLocation,
      address: e.target.value,
    })
  }

  // Handle next step in wizard
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  // Handle back step in wizard
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  // Create a new subscription
  const handleAddSubscription = async () => {
    if (!selectedLocation) {
      showAlert(t('notifications.errorLocationRequired', 'Please select a location first'), 'error')
      return
    }

    // Validate that address isn't empty for custom polygons
    if (selectedLocation.osm_data.type === 'polygon' && !selectedLocation.address) {
      showAlert(t('notifications.errorLocationNameRequired'), 'error')
      return
    }

    // Determine price range
    let minPrice, maxPrice
    if (showPriceRange) {
      minPrice = parseInt(customPriceMin, 10)
      maxPrice = parseInt(customPriceMax, 10)

      if (isNaN(minPrice) || isNaN(maxPrice) || minPrice > maxPrice) {
        showAlert(t('notifications.invalidPriceRange'), 'error')
        return
      }
    } else {
      // Use default price ranges
      if (propertyType === 'rent') {
        minPrice = PRICE_RENT_MIN_DEFAULT
        maxPrice = PRICE_RENT_MAX_DEFAULT
      } else {
        minPrice = PRICE_BUY_MIN_DEFAULT
        maxPrice = PRICE_BUY_MAX_DEFAULT
      }
    }

    setLoading(true)

    try {
      // Create subscription in Supabase
      const { data, error } = await supabase
        .from('notifications')
        .insert([
          {
            user_id: user.id,
            address: selectedLocation.address,
            country: selectedLocation.country,
            postcode: selectedLocation.postcode || '',
            type: propertyType,
            min_price: minPrice,
            max_price: maxPrice,
            enabled: true,
            osm_data: selectedLocation.osm_data,
            mapbox_data: {}, // Empty object as this is required but we're using OSM
          },
        ])
        .select()

      if (error) throw error

      if (data && data.length > 0) {
        onSuccess(data[0])
      } else {
        throw new Error('No data returned from subscription creation')
      }
    } catch (error) {
      console.error('Error creating subscription:', error)
      showAlert(t('notifications.errorAddSubscription'), 'error')
    } finally {
      setLoading(false)
    }
  }

  // Render steps in creation wizard
  const renderStep = (step) => {
    switch (step) {
      case 0: // Location selection step
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('notifications.selectLocation', 'Select Location')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t(
                'notifications.locationDescription',
                "Choose an area you're interested in. You can search for a specific location or draw a custom area on the map."
              )}
            </Typography>

            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              aria-label="location selection mode"
              sx={{ mb: 3 }}
            >
              <ToggleButton value="search" aria-label="search by address" sx={{ px: 3, py: 1 }}>
                <SearchIcon sx={{ mr: 1 }} />
                {t('notifications.searchMode')}
              </ToggleButton>
              <ToggleButton value="map" aria-label="select on map" sx={{ px: 3, py: 1 }}>
                <MapIcon sx={{ mr: 1 }} />
                {t('notifications.mapMode')}
              </ToggleButton>
            </ToggleButtonGroup>

            {mode === 'search' && <LocationSearch onLocationSelected={handleLocationSelected} />}

            {mode === 'map' && <LocationMap onLocationSelected={handleLocationSelected} />}

            {selectedLocation && selectedLocation.osm_data.type === 'polygon' && (
              <TextField
                fullWidth
                label={t('notifications.locationName', 'Location Name')}
                placeholder={t('notifications.addressPlaceholder')}
                value={selectedLocation.address}
                onChange={handleAddressChange}
                sx={{ mb: 3, mt: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {selectedLocation && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>{t('notifications.locationSelected', 'Location selected')}:</strong>{' '}
                  {selectedLocation.address}
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={onCancel} sx={{ mr: 1 }}>
                {t('notifications.cancel')}
              </Button>
              <Button
                variant="contained"
                endIcon={<NavigateNextIcon />}
                onClick={handleNext}
                disabled={!selectedLocation}
              >
                {t('notifications.continue', 'Continue')}
              </Button>
            </Box>
          </Box>
        )

      case 1: // Property preferences step
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('notifications.propertyPreferences', 'Property Preferences')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t(
                'notifications.preferencesDescription',
                'What type of properties are you looking for? Set your preferences to get notified about relevant listings.'
              )}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('notifications.propertyType', 'Property Type')}
              </Typography>
              <ToggleButtonGroup
                value={propertyType}
                exclusive
                onChange={handlePropertyTypeChange}
                aria-label="property type"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="rent" aria-label="properties for rent" sx={{ px: 3, py: 1 }}>
                  <ApartmentIcon sx={{ mr: 1 }} />
                  {t('notifications.toRent')}
                </ToggleButton>
                <ToggleButton value="buy" aria-label="properties for sale" sx={{ px: 3, py: 1 }}>
                  <HomeIcon sx={{ mr: 1 }} />
                  {t('notifications.toBuy')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2">{t('notifications.priceRange')}</Typography>
                <Button size="small" onClick={() => setShowPriceRange(!showPriceRange)}>
                  {showPriceRange
                    ? t('notifications.hidePriceRange')
                    : t('notifications.setPriceRange')}
                </Button>
              </Box>

              {!showPriceRange ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {t('notifications.usingDefaultRange', 'Using default price range for')}{' '}
                    {propertyType === 'rent'
                      ? t('notifications.rentals', 'rentals')
                      : t('notifications.purchases', 'purchases')}
                    :{' '}
                    <strong>
                      {propertyType === 'rent'
                        ? `€${PRICE_RENT_MIN_DEFAULT.toLocaleString()} - €${PRICE_RENT_MAX_DEFAULT.toLocaleString()}/month`
                        : `€${PRICE_BUY_MIN_DEFAULT.toLocaleString()} - €${PRICE_BUY_MAX_DEFAULT.toLocaleString()}`}
                    </strong>
                  </Typography>
                </Alert>
              ) : (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t(
                        'notifications.adjustPrice',
                        'Adjust the price range to receive notifications for properties within your budget'
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={t('notifications.minPrice')}
                      type="number"
                      value={customPriceMin || ''}
                      onChange={(e) => setCustomPriceMin(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={t('notifications.maxPrice')}
                      type="number"
                      value={customPriceMax || ''}
                      onChange={(e) => setCustomPriceMax(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>

            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('notifications.summary', 'Summary')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications.location', 'Location')}:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedLocation?.address}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications.propertyType', 'Property Type')}:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" fontWeight="medium">
                    {propertyType === 'rent' ? t('notifications.toRent') : t('notifications.toBuy')}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications.priceRange', 'Price Range')}:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" fontWeight="medium">
                    {showPriceRange
                      ? `€${customPriceMin || 0} - €${customPriceMax || 0}`
                      : propertyType === 'rent'
                      ? `€${PRICE_RENT_MIN_DEFAULT} - €${PRICE_RENT_MAX_DEFAULT}/month`
                      : `€${PRICE_BUY_MIN_DEFAULT} - €${PRICE_BUY_MAX_DEFAULT}`}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
                {t('notifications.back', 'Back')}
              </Button>
              <Box>
                <Button variant="outlined" onClick={onCancel} sx={{ mr: 1 }} disabled={loading}>
                  {t('notifications.cancel')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <NotificationsActiveIcon />}
                  onClick={handleAddSubscription}
                  disabled={loading}
                >
                  {loading
                    ? t('notifications.creating', 'Creating...')
                    : t('notifications.createSubscription', 'Create Alert')}
                </Button>
              </Box>
            </Box>
          </Box>
        )

      default:
        return null
    }
  }

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Button variant="text" startIcon={<ArrowBackIcon />} onClick={onCancel}>
          {t('notifications.backToAlerts', 'Back to Alerts')}
        </Button>

        <Typography variant="h5" sx={{ mt: 2, fontWeight: 600 }}>
          {t('notifications.createNewAlert', 'Create New Property Alert')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'notifications.createDescription',
            'Set up notifications for new listings that match your criteria'
          )}
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
        <Step>
          <StepLabel>{t('notifications.selectLocation', 'Select Location')}</StepLabel>
        </Step>
        <Step>
          <StepLabel>{t('notifications.setPreferences', 'Set Preferences')}</StepLabel>
        </Step>
      </Stepper>

      {renderStep(activeStep)}
    </Paper>
  )
}

export default AddSubscription
